import type { LearnFAQ } from "@/pages/learn/learnShared";
import {
  makeEsArticle,
  esSaintRelated,
  type EsArticleInput,
} from "./learnFactoryEs";

const reviewBlock = {
  writtenBy: "Neuro Wellness TMS Centers of America",
  reviewedBy: "Daniel Pietras, MD",
  published: "November 3, 2026",
  lastReviewed: "November 3, 2026",
  lastUpdated: "November 3, 2026",
};

const faqs: LearnFAQ[] = [
  {
    question: `¿Qué resultados mostró SAINT® en los ensayos clínicos?`,
    answer: `En el ensayo controlado aleatorizado de 2022, doble ciego y con control simulado, el 78,6 % del grupo de tratamiento activo alcanzó la remisión tras el protocolo de SAINT® de cinco días, frente al 13 % del grupo simulado (Cole et al., Am J Psychiatry 2022;179:132–141). Un estudio previo abierto informó una tasa de remisión similar (Cole et al., Am J Psychiatry 2020;177:716–726). Los resultados individuales varían; no se garantiza ningún resultado. Estos resultados aplican únicamente al sistema SAINT® (el Sistema de Neuromodulación Magnus autorizado por la FDA) y no se transfieren a otros protocolos de TMS acelerada.`,
  },
  {
    question: `¿Los resultados de SAINT® aplican a otros programas de TMS acelerada?`,
    answer: `No. La cifra de remisión del 78,6 % proviene de ensayos del sistema SAINT® específicamente — el Sistema de Neuromodulación Magnus autorizado por la FDA con su propio software de orientación guiada por fMRI. Otros protocolos de TMS acelerada pueden copiar el calendario, pero no usan el mismo sistema de orientación autorizado por la FDA, y sus propios proveedores reconocen que no se han probado en los ensayos clínicos de SAINT.`,
  },
  {
    question: `¿Dónde puedo leer reseñas de pacientes sobre SAINT® en Neuro Wellness?`,
    answer: `Puede leer reseñas verificadas de Google para Neuro Wellness TMS Centers of America en Coral Springs, Florida, en nuestro perfil de Google Business. Los resultados individuales varían; no se garantiza ningún resultado. Las historias de pacientes en esta página se comparten únicamente con consentimiento firmado y autorización HIPAA.`,
  },
];

export const esSaintTmsResults = makeEsArticle({
  slug: "saint-tms-results",
  title: `Resultados y reseñas de SAINT® TMS: lo que muestran los ensayos y los pacientes`,
  description: `Resultados de SAINT® TMS: remisión del 78,6 % en el ensayo aleatorizado de 2022 (Cole et al.), qué significa "remisión", historias de pacientes y reseñas de Google para la clínica de Coral Springs.`,
  h1: `Resultados de SAINT®: datos de los ensayos y experiencias de pacientes`,
  breadcrumbLabel: "Resultados y reseñas de SAINT® TMS",
  shortAnswer: `En el ensayo controlado aleatorizado de 2022, doble ciego y con control simulado, el 78,6 % del grupo de tratamiento activo alcanzó la remisión tras el protocolo de SAINT® de cinco días (Cole et al., Am J Psychiatry 2022;179:132–141). Los resultados individuales varían; no se garantiza ningún resultado. Estos resultados aplican al sistema SAINT® — el Sistema de Neuromodulación Magnus autorizado por la FDA con su propio software de orientación guiada por fMRI — y no se transfieren a otros protocolos de TMS acelerada. Si está en crisis, llame o envíe un mensaje de texto al 988.`,
  keyTakeaways: [
    `El 78,6 % del grupo activo alcanzó la remisión en el ensayo controlado aleatorizado de SAINT® de 2022 (Cole et al., 2022). Los resultados individuales varían.`,
    `El grupo simulado (placebo) tuvo una tasa de remisión del 13 % en el mismo ensayoño.`,
    `Remisión significa que la puntuación de depresión bajó por debajo de un umbral en una escala estandarizada (MADRS), no solo sentirse mejor.`,
    `Estos resultados aplican solo a SAINT® — el sistema Magnus autorizado por la FDA — no a otros programas de TMS acelerada.`,
    `Las historias y reseñas de pacientes se comparten únicamente con consentimiento firmado; no se fabrica ninguna historia.`,
    `Si está en crisis, llame o envíe un mensaje de texto al 988.`,
  ],
  sections: [
    {
      heading: "Lo que muestran los ensayos de SAINT®",
      paragraphs: [
        `SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) se ha estudiado en una serie de ensayos en Stanford. El más riguroso es el ensayo controlado aleatorizado de 2022, doble ciego y con control simulado: el 78,6 % de los participantes que recibieron SAINT® alcanzó la remisión tras el protocolo de cinco días, frente al 13 % de los del grupo simulado (Cole et al., Am J Psychiatry 2022;179:132–141). Un estudio previo abierto del mismo grupo informó una tasa de remisión similar (Cole et al., Am J Psychiatry 2020;177:716–726).`,
        `Remisión significa que la puntuación de depresión de un participante bajó por debajo de un umbral en una escala de ánimo estandarizada — en los ensayos de SAINT®, la Escala de Depresión Montgomery-Åsberg (MADRS). Una puntuación MADRS de 10 o menos generalmente indica remisión. La remisión es más estricta que la respuesta, que significa una mejoría significativa que no necesariamente alcanza el umbral de remisión. Los resultados individuales varían; no se garantiza ningún resultado.`,
        `Estos resultados aplican específicamente al sistema SAINT® — el Sistema de Neuromodulación Magnus autorizado por la FDA con su software de orientación guiada por fMRI (510(k) K220177, septiembre de 2022). Otros protocolos de TMS acelerada pueden copiar el calendario de cinco días con 10 sesiones diarias, pero no usan el mismo sistema de orientación autorizado por la FDA, y sus propios proveedores reconocen que no se han probado en los ensayos clínicos de SAINT. Los resultados de los ensayos de SAINT® no se transfieren a esos protocolos. [[Vea la comparación lado a lado|/es/learn/saint-vs-accelerated-tms]].`,
      ],
    },
    {
      heading: "Historias de pacientes",
      paragraphs: [
        `[PLACEHOLDER DE HISTORIA DE PACIENTE — Se añadirán aquí historias, citas y vídeo reales de pacientes únicamente con consentimiento firmado y autorización HIPAA. No se fabrica ninguna historia de paciente. Los resultados individuales varían; no se garantiza ningún resultado.]`,
      ],
    },
    {
      heading: "Reseñas de Google para la clínica de Coral Springs",
      paragraphs: [
        `Puede leer reseñas verificadas de Google para Neuro Wellness TMS Centers of America en Coral Springs, Florida, en nuestro perfil de Google Business. Las reseñas reflejan experiencias individuales y no garantizan ningún resultado. [[Lea nuestras reseñas de Google|/es/contactus]] o [[vea nuestra ubicación en Coral Springs|/es/tms-coral-springs-fl]].`,
      ],
      links: [
        {
          to: "/es/contactus",
          text: "Contáctenos para indicaciones y reseñas",
        },
      ],
    },
    {
      heading: "Qué significan estos resultados para usted",
      paragraphs: [
        `La tasa de remisión del 78,6 % es la evidencia publicada más sólida para SAINT®, pero es un resultado de grupo de un ensayo específico de un sistema específico en adultos con trastorno depresivo mayor cuya depresión no había mejorado lo suficiente con medicación antidepresiva previa. Su resultado individual depende de su diagnóstico, su historial y su evaluación. SAINT® está autorizado por la FDA para adultos de 22 años o más con trastorno depresivo mayor. [[Vea si puede ser candidato|/es/learn/who-qualifies-for-saint-tms]] o [[verifique sus beneficios del seguro|/es/check-insurance-options]]. SAINT® en Neuro Wellness comienza en $25.000; la cobertura varía según el plan y verificamos sus beneficios sin costo.`,
      ],
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://pubmed.ncbi.nlm.nih.gov/35624717/",
    },
    {
      citation:
        "Cole EJ, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for treatment-resistant depression. Am J Psychiatry. 2020;177(8):716-726.",
      url: "https://pubmed.ncbi.nlm.nih.gov/32463539/",
    },
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification — Magnus Neuromodulation System with SAINT Technology (K220177, September 2022).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
  ],
  relatedLinks: [
    {
      title: `SAINT® frente a la TMS acelerada de $9,900: no es el mismo tratamiento`,
      href: "/es/learn/saint-vs-accelerated-tms",
      description:
        "Por qué los resultados de los ensayos pertenecen solo a SAINT®",
    },
    {
      title: "¿Qué es SAINT® TMS?",
      href: "/es/learn/what-is-saint-tms",
      description:
        "El protocolo de 5 días guiado por fMRI autorizado por la FDA",
    },
    {
      title: "¿Quién califica para SAINT® TMS?",
      href: "/es/learn/who-qualifies-for-saint-tms",
      description: "Elegibilidad, listas y próximos pasos",
    },
    {
      title: "¿Es realmente SAINT®? Cómo verificar una clínica",
      href: "/es/learn/what-is-a-verified-saint-provider",
      description: "Una verificación de 3 pasos y señales de alerta",
    },
    {
      title: "Costo de SAINT® TMS",
      href: "/es/learn/saint-tms-cost",
      description: "Qué cuesta SAINT® y cómo planificar el pago",
    },
    {
      title: "SAINT® TMS acelerada",
      href: "/es/saint",
      description: "El protocolo de 5 días guiado por fMRI que ofrecemos",
    },
  ],
  reviewBlock,
  toc: [
    {
      label: "Lo que muestran los ensayos",
      href: "#lo-que-muestran-los-ensayos-de-saint",
    },
    { label: "Historias de pacientes", href: "#historias-de-pacientes" },
    {
      label: "Reseñas de Google",
      href: "#resenas-de-google-para-la-clinica-de-coral-springs",
    },
    {
      label: "Qué significan para usted",
      href: "#que-significan-estos-resultados-para-usted",
    },
    { label: "Preguntas frecuentes", href: "#faq" },
    { label: "Investigación citada", href: "#cited-research" },
  ],
  safetyBlock:
    "SAINT® no es para todos. Está contraindicado para personas con metal sensible a campos magnéticos no extraíble cerca de la cabeza, ciertos dispositivos implantados o presión intracraneal aumentada. Los posibles efectos secundarios incluyen cefalea, fatiga, molestias en el cuero cabelludo y ansiedad; la TMS conlleva un pequeño riesgo de convulsión y, como con cualquier tratamiento para la depresión, un riesgo de empeoramiento o pensamientos suicidas. Si está en crisis, llame o envíe un mensaje de texto al 988.",
  trademarkFooter:
    "SAINT® es una marca registrada de Stanford University, con licencia exclusiva a Magnus Medical, Inc. Neuro Wellness TMS Centers of America no está afiliada a Stanford University.",
  ctaBand: {
    headline: "Vea si SAINT® es para usted, sin costo",
    primaryText: "Vea si puedo ser candidato",
    primaryHref: "/es/learn/who-qualifies-for-saint-tms",
    secondaryText: "Verifique mi seguro",
    secondaryHref: "/es/check-insurance-options",
    phone: "954-827-9793",
  },
} satisfies EsArticleInput);
